import clsx from "clsx";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { Toggle } from "@/ui/atoms/Toggle/Toggle";
import { useAtom } from "jotai";
import {
    dataSourceOverridesAtom,
    DataSourceOverrides,
    MetricKey,
} from "../state";
import { DataSource } from "../hooks/useColorizedValue";
import { useState } from "react";
import { Accordion } from "@/ui/organisms/Accordion/Accordion";
import { motion } from "framer-motion";

const DATA_SOURCES = [
    {
        key: DataSource.FINANCE_API,
        label: "Finance API",
        color: "bg-blue-500",
        textColor: "text-blue-500",
    },
    {
        key: DataSource.LEGACY_BACKEND,
        label: "Legacy Backend",
        color: "bg-cyan-500",
        textColor: "text-cyan-500",
    },
    {
        key: DataSource.SFDC,
        label: "SFDC",
        color: "bg-green-500",
        textColor: "text-green-500",
    },
    {
        key: DataSource.FINANCE_LAB,
        label: "Finance Lab",
        color: "bg-purple-500",
        textColor: "text-purple-500",
    },
    {
        key: DataSource.MEMO_TEXT,
        label: "LLM Web API",
        color: "bg-orange-500",
        textColor: "text-orange-500",
    },
] as const;

const METRICS = [
    { key: MetricKey.REVENUE, label: "Revenue LTM" },
    { key: MetricKey.EBITDA, label: "EBITDA LTM" },
    { key: MetricKey.CASH, label: "Cash Balance" },
    { key: MetricKey.TOTAL_ASSETS, label: "Total Assets" },
    { key: MetricKey.TOTAL_LIABILITIES, label: "Total Liabilities" },
    { key: MetricKey.NET_INCOME, label: "Net Income" },
    { key: MetricKey.CRM_OPPORTUNITY_ID, label: "Opportunity ID" },
    { key: MetricKey.CRM_OPPORTUNITY_NAME, label: "Opportunity Name" },
] as const;

export function DataSourcePanel() {
    const flags = useFlags();
    const showDataSources = flags.enabled("show-data-sources");
    const showDataToggle = flags.enabled("show-data-toggle");
    const useFinanceLabDefault = flags.enabled("use-finance-lab-metrics");

    const [overrides, setOverrides] = useAtom(dataSourceOverridesAtom);
    const [legendOpen, setLegendOpen] = useState(true);
    const [overridesOpen, setOverridesOpen] = useState(true);
    const [isLeft, setIsLeft] = useState(false);

    if (!showDataSources && !showDataToggle) {
        return null;
    }

    const handleToggle = (metricKey: MetricKey, useFinanceLab: boolean) => {
        // CRM fields toggle between Finance Lab and SFDC
        const alternativeSource =
            metricKey === MetricKey.CRM_OPPORTUNITY_ID ||
            metricKey === MetricKey.CRM_OPPORTUNITY_NAME
                ? DataSource.SFDC
                : DataSource.FINANCE_API;

        setOverrides((prev: DataSourceOverrides) => ({
            ...prev,
            [metricKey]: useFinanceLab
                ? DataSource.FINANCE_LAB
                : alternativeSource,
        }));
    };

    const isMetricUsingFinanceLab = (metricKey: MetricKey): boolean => {
        if (overrides[metricKey]) {
            return overrides[metricKey] === DataSource.FINANCE_LAB;
        }
        return useFinanceLabDefault;
    };

    const handleDragEnd = (_event: any, info: any) => {
        const shouldBeLeft = info.point.x < window.innerWidth / 2;
        setIsLeft(shouldBeLeft);
    };

    const targetX = isLeft ? 16 : window.innerWidth - 280 - 16;

    return (
        <motion.div
            drag="x"
            dragElastic={0.2}
            dragConstraints={{ left: 16, right: window.innerWidth - 280 - 16 }}
            onDragEnd={handleDragEnd}
            animate={{ x: targetX }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
            className={clsx(
                "fixed left-0 top-4 z-[100] w-[280px]",
                "rounded-lg border border-slate-200 p-3 shadow-lg",
                "dark:border-slate-700 dark:bg-slate-800",
                "cursor-grab active:cursor-grabbing",
            )}
        >
            {showDataSources && (
                <Accordion
                    i="Data Sources"
                    isOpen={legendOpen}
                    onClick={() => setLegendOpen(!legendOpen)}
                >
                    <div className="flex flex-col gap-1 py-2">
                        {DATA_SOURCES.map((source) => (
                            <div
                                key={source.key}
                                className="flex items-center gap-2"
                            >
                                <div
                                    className={clsx(
                                        "size-3 rounded-full",
                                        source.color,
                                    )}
                                />
                                <span className="subdued body-micro">
                                    {source.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </Accordion>
            )}

            {showDataToggle && (
                <Accordion
                    i="Data Source Overrides"
                    isOpen={overridesOpen}
                    onClick={() => setOverridesOpen(!overridesOpen)}
                >
                    <div className="flex flex-col gap-2 pt-2">
                        {METRICS.map((metric) => {
                            const isFinanceLab = isMetricUsingFinanceLab(
                                metric.key,
                            );
                            const isCrmField =
                                metric.key === MetricKey.CRM_OPPORTUNITY_ID ||
                                metric.key === MetricKey.CRM_OPPORTUNITY_NAME;
                            const leftLabel = isCrmField ? "SFDC" : "API";

                            return (
                                <div
                                    key={metric.key}
                                    className="flex items-center justify-between gap-3"
                                >
                                    <span className="subdued body-micro">
                                        {metric.label}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={clsx(
                                                "subdued text-[10px]",
                                            )}
                                        >
                                            {leftLabel}
                                        </span>
                                        <Toggle
                                            checked={isFinanceLab}
                                            onChange={(checked) =>
                                                handleToggle(
                                                    metric.key,
                                                    checked,
                                                )
                                            }
                                        />
                                        <span
                                            className={clsx(
                                                "subdued text-[10px]",
                                            )}
                                        >
                                            Lab
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Accordion>
            )}
        </motion.div>
    );
}
